import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth, USER_ROLES } from './contexts/AuthContext';
import { ToastProvider } from './contexts/ToastContext';
import { ErrorBoundary } from './components/ErrorBoundary';
import { RequireAuth } from './components/routing/RequireAuth';
import ProtectedRoute from './components/common/ProtectedRoute';
import LandingPage from './pages/LandingPage';
import ContactPage from './pages/ContactPage';
import MapPage from './pages/MapPage';
import NotFoundPage from './pages/NotFoundPage';
import WelcomePage from './pages/WelcomePage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import SwitchPage from './pages/SwitchPage';
import LegalDocumentPage from './pages/LegalDocumentPage';
import AdminPortal from './components/portals/AdminPortal';
import BoardPortal from './components/portals/BoardPortal';
import MemberPortal from './components/portals/MemberPortal';
import ManagementPortal from './components/portals/ManagementPortal';
import { supabase } from './lib/supabase';
import { arrivedViaInvite, clearInviteArrival } from './lib/inviteDetection';
import { useHOA } from './contexts/HOAContext';
import { WrongAssociation } from './components/auth/WrongAssociation';

const PortalRouter = () => {
  const { isAuthenticated, getEffectiveRole, effectiveRole, tenantStatus } = useAuth();
  const { isLoading: hoaLoading } = useHOA();
  // null = still checking, true = route to onboarding, false = proceed normally
  const [needsOnboarding, setNeedsOnboarding] = useState(arrivedViaInvite() ? null : false);

  useEffect(() => {
    if (!arrivedViaInvite()) return;
    let mounted = true;
    const checkOnboarding = async () => {
      try {
        const { data: { user: authUser } } = await supabase.auth.getUser();
        const complete = authUser?.user_metadata?.onboarding_complete === true;
        if (complete) clearInviteArrival();
        if (mounted) setNeedsOnboarding(!complete && !!authUser);
      } catch (e) {
        if (mounted) setNeedsOnboarding(false);
      }
    };
    checkOnboarding();
    return () => { mounted = false; };
  }, []);

  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  // tenantStatus gates the portal until the account is matched to this tab's
  // association, so no portal query runs before the Super Admin header is set.
  if (hoaLoading || needsOnboarding === null || tenantStatus !== 'ready') {
    return (
      <div className="min-h-screen bg-main-bg flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-secondary" />
      </div>
    );
  }

  if (needsOnboarding) {
    return <Navigate to="/welcome" replace />;
  }

  // Route users to appropriate portal based on effective role (impersonation-aware)
  switch (effectiveRole ?? getEffectiveRole()) {
    case USER_ROLES.ADMIN:
      return <ErrorBoundary><AdminPortal /></ErrorBoundary>;
    case USER_ROLES.MANAGEMENT:
      return <ErrorBoundary><ManagementPortal /></ErrorBoundary>;
    case USER_ROLES.BOARD:
      return <ErrorBoundary><BoardPortal /></ErrorBoundary>;
    case USER_ROLES.MEMBER:
      return <ErrorBoundary><MemberPortal /></ErrorBoundary>;
    default:
      return <Navigate to="/portal" replace />;
  }
};



// A resident who signed in on another association's address sees where to go.
const TenantMismatchGate = ({ children }) => {
  const { tenantMismatch, clearTenantMismatch } = useAuth();
  if (tenantMismatch) {
    return <WrongAssociation mismatch={tenantMismatch} onDismiss={clearTenantMismatch} />;
  }
  return children;
};

function App() {
  return (
    <ToastProvider>
      <AuthProvider>
        <Router>
          <div className="App">
            <TenantMismatchGate>
            <Routes>
              {/* Public Routes */}
              <Route path="/" element={<LandingPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/map" element={<MapPage />} />

              {/* New-member onboarding (requires the session from the invite link) */}
              <Route path="/welcome" element={<ErrorBoundary><WelcomePage /></ErrorBoundary>} />

              {/* Super Admin association switch: one-time token in the fragment */}
              <Route path="/switch" element={<ErrorBoundary><SwitchPage /></ErrorBoundary>} />
              {/* Published legal documents (public by design, like /contact; no RequireAuth) */}
              <Route path="/legal/:docSlug" element={<ErrorBoundary><LegalDocumentPage /></ErrorBoundary>} />

              {/* Password recovery landing (public; the emailed link carries the session) */}
              <Route path="/reset-password" element={<ErrorBoundary><ResetPasswordPage /></ErrorBoundary>} />

              {/* Portal Authentication Route — dispatches by role */}
              <Route
                path="/portal"
                element={
                  <ProtectedRoute>
                    <PortalRouter />
                  </ProtectedRoute>
                }
              />

              {/* Role-specific Portal Routes */}
              <Route
                path="/portal/admin/*"
                element={
                  <RequireAuth allowedRoles={[USER_ROLES.ADMIN]}>
                    <ErrorBoundary><AdminPortal /></ErrorBoundary>
                  </RequireAuth>
                }
              />
              <Route
                path="/portal/management/*"
                element={
                  <RequireAuth allowedRoles={[USER_ROLES.MANAGEMENT, USER_ROLES.ADMIN]}>
                    <ErrorBoundary><ManagementPortal /></ErrorBoundary>
                  </RequireAuth>
                }
              />
              <Route
                path="/portal/board/*"
                element={
                  <RequireAuth allowedRoles={[USER_ROLES.BOARD, USER_ROLES.ADMIN]}>
                    <ErrorBoundary><BoardPortal /></ErrorBoundary>
                  </RequireAuth>
                }
              />
              <Route
                path="/portal/member/*"
                element={
                  <RequireAuth allowedRoles={[USER_ROLES.MEMBER, USER_ROLES.ADMIN]}>
                    <ErrorBoundary><MemberPortal /></ErrorBoundary>
                  </RequireAuth>
                }
              />

              {/* Legacy direct routes (backward compat) */}
              <Route
                path="/admin"
                element={
                  <ProtectedRoute requiredRole={USER_ROLES.ADMIN}>
                    <ErrorBoundary><AdminPortal /></ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/board"
                element={
                  <ProtectedRoute requiredRole={USER_ROLES.BOARD}>
                    <ErrorBoundary><BoardPortal /></ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/member"
                element={
                  <ProtectedRoute requiredRole={USER_ROLES.MEMBER}>
                    <ErrorBoundary><MemberPortal /></ErrorBoundary>
                  </ProtectedRoute>
                }
              />

              {/* 404 */}
              <Route path="/404" element={<NotFoundPage />} />
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
            </TenantMismatchGate>
          </div>
        </Router>
      </AuthProvider>
    </ToastProvider>
  );
}

export default App;
